// Changes to 3Board.jsx to implement starred moves and flip board functionality

import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef, useContext } from 'react';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import useGameLogic from './UseGameState';
import FENBuilderModal from '../modals/FENBuilderModal';
import { BOARD_THEMES, DEFAULT_MOVE_HIGHLIGHT } from './BoardStyle';
import constants from '../../util/constants';
import { isProfane, cleanProfanity } from '../../util/filterProfanity';
import { MobileContext } from '../../context/MobileContext';
// Import GG icons for checkmate, check and draw states
import winnerIcon from '../../images/GGIcons/Winner.png';
import loserIcon from '../../images/GGIcons/Loser.png';
import checkIcon from '../../images/GGIcons/Check.png';
import drawIcon from '../../images/GGIcons/Draw.png';

// Import StockfishIcons for Record mode move classifications
import BestIcon from '../../images/StockfishIcons/Best.png';
import BlunderIcon from '../../images/StockfishIcons/Blunder.png';
import BrillianceIcon from '../../images/FooterIcons/Brilliance2.png';
import ExcellentIcon from '../../images/StockfishIcons/Excellent.png';
import GoodIcon from '../../images/StockfishIcons/Good.png';
import GreatIcon from '../../images/StockfishIcons/Great.png';
import InaccurateIcon from '../../images/StockfishIcons/Inaccurate.png';
import MissIcon from '../../images/StockfishIcons/Miss.png';
import MistakeIcon from '../../images/StockfishIcons/Mistake.png';
import TheoryIcon from '../../images/StockfishIcons/Theory.png';

const CHESSBOARD = constants.CHESSBOARD;

// StockfishIcon mapping for Record mode classifications
const STOCKFISH_ICON_MAP = {
  'best': BestIcon,
  'brilliant': BrillianceIcon,
  'great': GreatIcon,
  'excellent': ExcellentIcon,
  'good': GoodIcon,
  'theory': TheoryIcon,
  'inaccurate': InaccurateIcon,
  'mistake': MistakeIcon,
  'miss': MissIcon,
  'blunder': BlunderIcon
};

// Define click-to-move styling constants
const SELECTED_SQUARE_COLOR = { boxShadow: 'inset 0 0 0 4px #FFD700' }; // Gold highlight for selected piece
const LEGAL_MOVE_CIRCLE_CAPTURE = 'radial-gradient(circle, rgba(0,0,0,.1) 85%, transparent 85%)'; // Circle for captures
const LEGAL_MOVE_CIRCLE_NORMAL = 'radial-gradient(circle, rgba(0,0,0,.1) 25%, transparent 25%)'; // Circle for normal moves

const Board = forwardRef(({ 
  boardSize = 250, 
  boardTheme = 'blue', 
  showBoardNotation,
  // Add puzzle-related props
  isPuzzle = false,
  onValidateMove = null,
  shouldAllowVariation = () => true,
  puzzleState = null,
  gameHasPuzzleTag = false,
  // Add solution data and completion callbacks
  gameMetadata = null,
  revealedMoves = [],
  setRevealedMoves = null,
  setLastRevealedMove = null,
  setPuzzleState = null,
  setShowGameSettings = null,
  // Add current move tracking
  currentMoveIdx = null,
  setCurrentMoveIdx = null,
  // Add commenting mode
  isCommenting = false,
  // Add mode to distinguish between ViewGame and AddGame
  isViewMode = false,
  // Add initial player data props
  initialPOVPlayer = null,
  initialOppositePlayer = null,
  onPlayerDataChange = null,
  // Add prop to control showing collapsed icons on board
  showCollapsedIcons = false,
  // Add callback for starting variation limit
  onStartingVariationLimit = null,
  // Mobile diagram mode support
  activeColor: mobileActiveColor = null,
  mobileArrowMode = false,
  mobileArrowColor = null,
  mobileArrowStart = null,
  onMobileArrowStart = null,
  // Add callback for new move detection
  onNewMove = null,
  // Record mode support
  isRecordMode = false,
  moveEnhancements = {},
  // External custom square styles support
  customSquareStyles: externalCustomSquareStyles = null
}, ref) => {
  const { 
    moves, 
    currentMove, 
    currentFEN, 
    recordMove, 
    getNextMove,
    goToMove, 
    dispatch,
    setInitialPosition,
    getMoveHistory,
    state,
    orientation
  } = useGameLogic();
  
  // Get mobile context for responsive styling
  const isMobile = useContext(MobileContext);
  
  // Calculate responsive board size - respect the boardSize prop even on mobile
  const responsiveBoardSize = isMobile ? Math.min(boardSize, window.innerWidth - 20) : boardSize;
  
  // Create responsive styles for mobile
  const getPlayerInfoStyles = (isExpanded, position) => {
    const baseStyle = {
      position: 'absolute',
      backgroundColor: isExpanded ? 'rgba(255, 215, 0, 0.9)' : 'rgba(255, 215, 0, 1)',
      borderRadius: isExpanded ? '5px' : '50%',
      padding: isExpanded ? (isMobile ? '6px' : '8px') : (isMobile ? '2px' : '4px'),
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 5000,
      transition: 'all 0.3s ease',
      width: isExpanded ? 'auto' : (isMobile ? '24px' : '24px'),
      height: isExpanded ? 'auto' : (isMobile ? '24px' : '24px'),
      minHeight: isExpanded ? (isMobile ? '30px' : '30px') : (isMobile ? '24px' : '24px'),
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      border: 'none'
    };
    
    // Position-specific styles - make positioning identical
    if (position === 'top') {
      return {
        ...baseStyle,
        top: isExpanded ? (isMobile ? '8px' : '8px') : (isMobile ? '-12px' : '-12px'),
        right: isExpanded ? (isMobile ? '8px' : '8px') : (isMobile ? '-12px' : '-12px')
      };
    } else {
      return {
        ...baseStyle,
        bottom: isExpanded ? (isMobile ? '8px' : '8px') : (isMobile ? '-12px' : '-12px'),
        left: isExpanded ? (isMobile ? '8px' : '8px') : (isMobile ? '-12px' : '-12px')
      };
    }
  };
  
  // Input styles for mobile
  const getInputStyles = (type) => {
    const baseStyle = {
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      padding: '2px',
      color: '#000'
    };
    
    if (type === 'player') {
      return {
        ...baseStyle,
        fontSize: isMobile ? '12px' : '14px',
        fontWeight: 'bold',
        width: isMobile ? '80px' : '100px'
      };
    } else {
      return {
        ...baseStyle,
        fontSize: isMobile ? '11px' : '12px',
        width: isMobile ? '40px' : '50px'
      };
    }
  };
  
  // TEST: Debug dispatch function on component mount
  useEffect(() => {
    // console.log('🔍 [3Board] Testing dispatch function on mount');
    // console.log('🔍 [3Board] dispatch:', dispatch, typeof dispatch);
    if (dispatch && typeof dispatch === 'function') {
      // console.log('🔍 [3Board] Testing dispatch with SET_ORIENTATION action');
      dispatch({ type: 'SET_ORIENTATION', payload: orientation });
      
      // Also test with a simple action to see if the issue is specific to our action
      // console.log('🔍 [3Board] Testing dispatch with simple TEST_DISPATCH action');
      dispatch({ type: 'TEST_DISPATCH', payload: 'test' });
    }
  }, []);
  const [game, setGame] = useState(new Chess());
  const lastMoveRef = useRef('');
  const pendingOrientationRef = useRef(null);
  const lastDispatchedFenRef = useRef(''); // Add ref to track last dispatched FEN

  // Add state for arrows and highlighted squares
  const [activeColor, setActiveColor] = useState('#ff0050'); // Default red color
  const [moveSquares, setMoveSquares] = useState({});
  const [userDefinedSquares, setUserDefinedSquares] = useState({});
  const [userArrows, setUserArrows] = useState([]);
  const [stockfishArrows, setStockfishArrows] = useState([]);
  const previousMoveRef = useRef(null);

  // Add click-to-move state variables
  const [moveFrom, setMoveFrom] = useState('');
  const [moveTo, setMoveTo] = useState(null);
  const [optionSquares, setOptionSquares] = useState({});
  const [showLegalMoves, setShowLegalMoves] = useState(() => {
    // Get preference from localStorage, default to true for better UX
    const saved = localStorage.getItem('showLegalMoves');
    return saved !== null ? saved === 'true' : true;
  });

  // Add state for player info expansion with localStorage persistence
  const [playerInfoW, setPlayerInfoW] = useState(() => {
    // In ViewMode, use initial data from gameMetadata, otherwise start fresh
    if (isViewMode) {
      return {
        expanded: localStorage.getItem('playerInfoW_expanded') === 'true',
        player: initialOppositePlayer?.[0] || 'Anonymous',
        rating: initialOppositePlayer?.[1] || '200?'
      };
    }
    // In AddGame mode, always start with empty data (no localStorage persistence for player data)
    return {
      expanded: localStorage.getItem('playerInfoW_expanded') === 'true',
      player: '', // Always start empty in AddGame mode
      rating: '' // Always start empty in AddGame mode
    };
  });
  const [playerInfoB, setPlayerInfoB] = useState(() => {
    // In ViewMode, use initial data from gameMetadata, otherwise start fresh
    if (isViewMode) {
      return {
        expanded: localStorage.getItem('playerInfoB_expanded') === 'true',
        player: initialPOVPlayer?.[0] || 'Anonymous',
        rating: initialPOVPlayer?.[1] || '200?'
      };
    }
    // In AddGame mode, always start with empty data (no localStorage persistence for player data)
    return {
      expanded: localStorage.getItem('playerInfoB_expanded') === 'true',
      player: '', // Always start empty in AddGame mode
      rating: '' // Always start empty in AddGame mode
    };
  });

  // Add state variables for king positions and game state tracking
  const [kingPositions, setKingPositions] = useState({ white: null, black: null });
  const [gameState, setGameState] = useState({
    isCheckmate: false,
    isCheck: false,
    isDraw: false,
    winner: null
  });
  const [squareSize, setSquareSize] = useState(0); // For icon positioning

  // Get the current theme colors
  const currentTheme = BOARD_THEMES[boardTheme] || BOARD_THEMES.blue;

  // Get custom colors from localStorage if the theme is 'custom'
  const [customColors] = useState(() => {
    if (boardTheme === 'custom') {
      const savedCustomColors = localStorage.getItem('customColors');
      return savedCustomColors ? JSON.parse(savedCustomColors) : {
        dark: '#3066be',
        light: '#eeeed2'
      };
    }
    return null;
  });

  // Get coordinates preference from localStorage if not provided as prop
  const shouldShowCoordinates = showBoardNotation ?? 
    (localStorage.getItem('showCoordinates') === null ? true : localStorage.getItem('showCoordinates') === 'true');

  // Event listeners for keyboard modifiers to change square highlight colors
  const handleKeyDown = (event) => {
    let newColor = '#ff0050'; // Default red
    switch (event.key) {
      case "1":
        newColor = '#00aaff'; // Blue
        break;
      case "2":
        newColor = '#58b047'; // Green
        break;
      case "3":
        newColor = '#ffcc00'; // Yellow
        break;
      default:
        newColor = '#ff0050'; // Default red
    }
    setActiveColor(newColor);
  };

  const handleKeyUp = () => {
    setActiveColor('#ff0050'); // Revert to default red
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Calculate square size when board size changes (responsive)
  useEffect(() => {
    // Square size is the board size divided by 8 (8x8 chess board)
    setSquareSize(responsiveBoardSize / 8);
  }, [responsiveBoardSize]);

  // Puzzle mode detection - clear engine arrows to prevent spoiling solution
  useEffect(() => {
    if (gameHasPuzzleTag || isPuzzle) {
      // console.log('[3Board] Puzzle mode detected - clearing engine arrows');
      setStockfishArrows([]); // Clear all engine arrows in puzzle mode
    }
  }, [gameHasPuzzleTag, isPuzzle]);

  // Sync internal useGameLogic state with external currentMoveIdx prop
  useEffect(() => {
    if (currentMoveIdx !== null && currentMoveIdx !== undefined && 
        currentMoveIdx !== currentMove && setCurrentMoveIdx) {
      
      
      // Use the dispatch function from useGameLogic to update the move
      if (dispatch && typeof dispatch === 'function') {
        dispatch({ type: 'GOTO_MOVE', payload: currentMoveIdx });
      }
    }
  }, [currentMoveIdx, currentMove, dispatch]);

  // Watch for position changes and dispatch FEN events for live analysis
  useEffect(() => {
    if (game && game.fen) {
      const currentFEN = game.fen();
      
      // Only dispatch if the FEN has actually changed
      if (currentFEN !== lastDispatchedFenRef.current) {
        
        lastDispatchedFenRef.current = currentFEN; // Update the ref
        
        // Dispatch board-fen-updated event for position changes
        window.dispatchEvent(new CustomEvent('board-fen-updated', { 
          detail: { 
            fen: currentFEN,
            source: 'board-component-navigation',
            currentMove: currentMove,
            timestamp: Date.now() 
          } 
        }));
        
        // Call the ViewGame handler if it exists
        if (window.viewGameHandleFenChange && typeof window.viewGameHandleFenChange === 'function') {
          
          window.viewGameHandleFenChange(currentFEN);
        }
        
        // Update global window.currentFEN
        window.currentFEN = currentFEN;
      }
    }
  }, [game, currentMove]); // Watch for changes in game state and current move

  // Handle FEN updates from FENBuilderModal
  const handleFENUpdate = (fen, gameResult) => {
    

    try {
      if (!fen) {
        
        return false;
      }
      
      // Create a new Chess instance with this FEN
      const newGame = new Chess();
      newGame.load(fen);
      
      
      
      // First update the local game state
      setGame(newGame);
      
      // CRITICAL FIX: Only update initialPosition when we're actually changing the starting position
      // Don't update it during normal move navigation, as this corrupts the true starting position
      // setInitialPosition should only be called when setting up a custom starting position
      // setInitialPosition(fen);
      
      // Clear the last move reference to allow any move after a position update
      lastMoveRef.current = '';
      
      

      // Dispatch a custom event to notify other components of the FEN update
      window.dispatchEvent(new CustomEvent('board-fen-updated', { 
        detail: { 
          fen: newGame.fen(),
          source: 'board-component',
          timestamp: Date.now() 
        } 
      }));
      
      // Also call the global handler directly if it exists
      if (window.addGameHandleFenChange && typeof window.addGameHandleFenChange === 'function') {
        
        window.addGameHandleFenChange(newGame.fen());
      }
      
      // Also call the ViewGame handler if it exists
      if (window.viewGameHandleFenChange && typeof window.viewGameHandleFenChange === 'function') {
        
        window.viewGameHandleFenChange(newGame.fen());
      }
      
      // Trigger Stockfish analysis for this position
      triggerStockfishAnalysis(newGame.fen());
      


      // Update game state based on chess position and move notation
      updateGameState(newGame, gameResult);

      
      return true;
    } catch (error) {
      console.error('🔍 BOARDBUG [Board.handleFENUpdate] ERROR:', {
        error: error.message,
        fen,
        stack: error.stack
      });
      return false;
    }
  };

  // Function to trigger Stockfish analysis for the current position
  const triggerStockfishAnalysis = (fen) => {
    try {
      
      
      // Get the current move index to associate analysis with the correct move
      const currentMoveId = window.getCurrentMoveIndex ? window.getCurrentMoveIndex() : state.currentMove;
      
      // Store the FEN in the current FEN global for recovery if needed
      window.currentFEN = fen;
      
      // Check if we have a ChessAnalysisContext function available
      if (window.updatePositionForAnalysis && typeof window.updatePositionForAnalysis === 'function') {
        window.updatePositionForAnalysis(fen);
        return true;
      }
      
      // Fallback: dispatch an event for Stockfish to catch
      window.dispatchEvent(new CustomEvent('request-stockfish-analysis', {
        detail: {
          fen: fen,
          moveId: currentMoveId,
          source: 'board-component',
          forceCalculation: false, // Set to true to force recalculation
          timestamp: Date.now()
        }
      }));
      
      return true;
    } catch (error) {
      console.error('[Board] Error triggering Stockfish analysis:', error);
      return false;
    }
  };

  // Clear last move reference - useful for undo operations
  const clearLastMoveRef = () => {
    
    lastMoveRef.current = '';
  };

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    handleFENUpdate,
    clearLastMoveRef,
      // Add a direct method to update the board's FEN for external components
  updateBoardFEN: (fen) => {
    handleFENUpdate(fen);
  },
    // Add a method to get the current FEN
    getCurrentFEN: () => {
      return game ? game.fen() : null;
    },
    // Add a method to update orientation
    updateOrientation: (newOrientation) => {
      
      if (dispatch && typeof dispatch === 'function') {
        dispatch({ type: 'SET_ORIENTATION', payload: newOrientation });
        return true;
      }
      return false;
    },
    // Add a method to force open both player panels
    forceOpenBothPlayerPanels: forceOpenBothPlayerPanels,
    // Add a method to close both player panels
    closeBothPlayerPanels: closeBothPlayerPanels,
    // Add a method to check if both panels are expanded
    areBothPlayerPanelsExpanded: areBothPlayerPanelsExpanded,
    // Add a method to clear internal player data (for new games)
    clearInternalPlayerData: clearInternalPlayerData,
    // Add a method to get current player data
    getCurrentPlayerData: () => ({
      povPlayer: [playerInfoB.player, playerInfoB.rating],
      oppositePlayer: [playerInfoW.player, playerInfoW.rating]
    }),
    // Add method to toggle legal moves display
    toggleLegalMoves: () => {
      setShowLegalMoves(prev => {
        const newValue = !prev;
        localStorage.setItem('showLegalMoves', newValue.toString());
        return newValue;
      });
    },
    // Add method to get current legal moves setting
    getShowLegalMoves: () => showLegalMoves,
    // Add method to clear click-to-move selection
    clearMoveSelection: () => {
      setMoveFrom('');
      setMoveTo(null);
      setOptionSquares({});
    },
    // Add methods to directly clear visual elements (for CLEAR button)
    clearAllArrows: () => {
      // console.log('🔄 [3Board] Directly clearing all arrows via ref');
      setUserArrows([]);
    },
    clearAllSquares: () => {
      // console.log('🔄 [3Board] Directly clearing all highlighted squares via ref');
      setUserDefinedSquares({});
    },
    // Add method to clear all visuals at once
    clearAllVisuals: () => {
      // console.log('🔄 [3Board] Directly clearing all visuals via ref');
      setUserArrows([]);
      setUserDefinedSquares({});
      setMoveSquares({});
    }
  }));

  // Listen for global FEN update events from other components
  useEffect(() => {
    const handleExternalFENUpdate = (event) => {
      // Skip updates from Stockfish that are just analysis completions
      if (event.detail.isAnalysisOnly) {
        
        return;
      }
      
      // Only process events that didn't come from this component
      if (event.detail.source !== 'board-component') {
        
        // Use the current value of handleFENUpdate via ref.current with null check
        if (ref && ref.current && ref.current.updateBoardFEN) {
          ref.current.updateBoardFEN(event.detail.fen);
        } else {
          // Fallback - create a new Chess instance and update manually
          try {
            const newGame = new Chess(event.detail.fen);
            setGame(newGame);
            // CRITICAL FIX: Don't update initialPosition during navigation
            // setInitialPosition(event.detail.fen);
          } catch (e) {
            console.error("[Board] Failed to update FEN in event handler:", e);
          }
        }
      }
    };
    
    const handleRootFENChanged = (event) => {
      
      
      // Just handle the FEN update here - use the ref to access the current function with null check
      if (ref && ref.current && ref.current.updateBoardFEN) {
        ref.current.updateBoardFEN(event.detail.fen);
              } else {
          // Fallback - create a new Chess instance and update manually
          try {
            const newGame = new Chess(event.detail.fen);
            setGame(newGame);
            // CRITICAL FIX: Don't update initialPosition during navigation
            // setInitialPosition(event.detail.fen);
          } catch (e) {
            console.error("[Board] Failed to update FEN in event handler:", e);
          }
        }
      
      // Store orientation in ref for later processing with null check
      if (pendingOrientationRef && pendingOrientationRef.current && event.detail.orientation) {
        pendingOrientationRef.current = event.detail.orientation;
      }
    };
    
    // Handle cases where Stockfish tries to reset the board position
    const handleStockfishPositionPreserved = (event) => {
      
      // Force update the current FEN state to match what should be displayed
      if (event.detail.fen && event.detail.fen !== currentFEN) {
        if (ref && ref.current && ref.current.updateBoardFEN) {
          ref.current.updateBoardFEN(event.detail.fen);
        }
      }
    };
    
          // Add listener for force-board-position events (for analysis position recovery)
      const handleForceBoardPosition = (event) => {
        if (event.detail && event.detail.fen) {
          
          // Use the handleFENUpdate method to force the position
          handleFENUpdate(event.detail.fen);
        }
      };
      
      // Add listener for Stockfish move selections
      const handleStockfishMoveSelected = (event) => {
        if (event.detail && event.detail.move) {
          
          
          try {
            // Create a new game instance to avoid modifying state directly
            const gameCopy = new Chess(game.fen());
            
            // Make the move using LAN notation
            const move = gameCopy.move(event.detail.move);
            if (move) {
              
              
              // Puzzle move validation - check if move is correct before allowing it
              if (isPuzzle && onValidateMove) {
                
                const isValidPuzzleMove = onValidateMove(move);
                if (!isValidPuzzleMove) {
                  
                  return false; // Prevent the move from being recorded
                }
                
              } else {
                
              }
              
              // Only track non-duplicate moves in normal gameplay
              if (move.san === lastMoveRef.current) {
                
                return true;
              }
              
              lastMoveRef.current = move.san;
              
              // First update the local game state to ensure proper rendering
              setGame(gameCopy);
              
              // Highlight the source and target squares
              const highlightSquares = {
                [move.from]: currentTheme.moveFrom || DEFAULT_MOVE_HIGHLIGHT.moveFrom,
                [move.to]: currentTheme.moveTo || DEFAULT_MOVE_HIGHLIGHT.moveTo
              };
              setMoveSquares(highlightSquares);
              
              // Get the new FEN position after the move
              const newFEN = gameCopy.fen();
              
              // Update game state with move notation for check/checkmate detection
              updateGameState(gameCopy, move.san);
              
              // Call the global FEN handler to trigger opening detection
              if (window.addGameHandleFenChange && typeof window.addGameHandleFenChange === 'function') {
                
                window.addGameHandleFenChange(newFEN);
              }
              
              // Also call the ViewGame handler if it exists
              if (window.viewGameHandleFenChange && typeof window.viewGameHandleFenChange === 'function') {
                
                window.viewGameHandleFenChange(newFEN);
              }
              
              // Dispatch board-fen-updated event for move changes
              
              window.dispatchEvent(new CustomEvent('board-fen-updated', { 
                detail: { 
                  fen: newFEN,
                  source: 'board-component-move',
                  move: move.san,
                  timestamp: Date.now() 
                } 
              }));
              
              // Only trigger Stockfish analysis if not explicitly skipped (for sequential moves)
              if (!event.detail.skipAnalysis) {
                
                triggerStockfishAnalysis(newFEN);
              } else {
                
              }
              
              // NOTE: Visual elements will be cleared and reloaded by the currentMove useEffect
              // when the move is recorded and currentMove changes
              
              // Prepare additional data for the move with all required fields
              // NOTE: We capture empty arrays since visuals will be managed by the move navigation system
              const additionalData = { 
                fen: newFEN,
                startSquare: move.from,
                endSquare: move.to,
                arrows: [], // Empty - visuals managed by navigation system
                highlightedSquares: {}, // Empty - visuals managed by navigation system
                content: "" // Initialize empty content for later editing
              };
              
              // STOCKFISH MOVE DETECTION: Follow the same pattern as drag/drop moves
              // Call onNewMove first (like drag/drop does), then check for existing moves
              
              // Call new move detection callback if provided (same as drag/drop flow)
              if (onNewMove && typeof onNewMove === 'function') {
                // console.log('🔍 Stockfish move - calling onNewMove like drag/drop does');
                onNewMove(move.san, move.from, move.to, 'stockfish');
              }
              
              // Check if this move already exists at the next position OR as a variation
              const nextMove = getNextMove();
              
              // First check the next move in the current line
              if (nextMove && nextMove.move.notation === move.san) {
                // console.log('🔍 Stockfish move matches next move in current line:', nextMove.id);
                goToMove(nextMove.id);
                return true; // Exit early - existing move, navigate to it
              }
              
              // If not the next move, check for variations from current position
              let variationMatch = null;
              
              if (currentMove === '-1') {
                // Check start position variations
                if (gameState?.startPosition?.variations) {
                  gameState.startPosition.variations.forEach((variation, varIndex) => {
                    if (variation && variation.length > 0 && variation[0].notation === move.san) {
                      variationMatch = { path: `-1|${varIndex}:0`, notation: move.san };
                    }
                  });
                }
              } else if (!currentMove.includes('|')) {
                // We're on main line - check variations from this move
                const currentIndex = parseInt(currentMove, 10);
                if (!isNaN(currentIndex) && moves[currentIndex]?.variations) {
                  moves[currentIndex].variations.forEach((variation, varIndex) => {
                    if (variation && variation.length > 0 && variation[0].notation === move.san) {
                      variationMatch = { path: `${currentIndex}|${varIndex}:0`, notation: move.san };
                    }
                  });
                }
              }
              
              if (variationMatch) {
                // console.log('🔍 Stockfish move matches variation:', variationMatch.path);
                goToMove(variationMatch.path);
                return true; // Exit early - existing move, navigate to it
              }
              
              // If we reach here, this is a NEW move that doesn't exist yet
              // Continue with normal flow (like drag/drop does) - recordMove, puzzle validation, etc.
              
              // Add comment metadata if in commenting mode
              if (isCommenting) {
                additionalData.creator = {
                  id: -1, // Temporary ID for unsaved comments
                  type: CHESSBOARD.MOVE_TYPES.COMMENT
                };
                
              } else {
                
              }
              
              // Then record the move in the global state, including the resulting FEN and comment metadata
              recordMove(move.san, move.from, move.to, additionalData);
              
              // Update the current FEN global for reference
              window.currentFEN = newFEN;
              
              
              
              // After recording a move, manually check for opening information after a small delay
              // to ensure state is updated
              setTimeout(() => {
                if (window.addGameHandleFenChange) {
                  
                  window.addGameHandleFenChange(newFEN);
                }
              }, 300);
              
              return true;
            } else {
              console.error('[Board] Failed to apply Stockfish move:', event.detail.move);
              return false;
            }
          } catch (error) {
            console.error('[Board] Error applying Stockfish move:', error);
            return false;
          }
        }
      };
    
    // Add handler for Stockfish arrows from StockfishAnalysisPanel
    const handleStockfishArrowsUpdate = (event) => {
      if (event.detail && event.detail.arrows) {
        // console.log('[3Board] Received stockfish arrows update:', event.detail.arrows);
        // PUZZLE MODE: Block engine arrows to prevent spoiling the solution
        if (gameHasPuzzleTag) {
          // console.log('[3Board] Blocking engine arrows in puzzle mode');
          setStockfishArrows([]); // Clear any existing arrows
          return;
        }
        setStockfishArrows(event.detail.arrows);
      }
    };

    window.addEventListener('board-fen-updated', handleExternalFENUpdate);
    window.addEventListener('board-root-fen-changed', handleRootFENChanged);
    window.addEventListener('stockfish-position-preserved', handleStockfishPositionPreserved);
    window.addEventListener('force-board-position', handleForceBoardPosition);
    window.addEventListener('stockfish-move-selected', handleStockfishMoveSelected);
    window.addEventListener('stockfish-arrows-update', handleStockfishArrowsUpdate);
    
    return () => {
      window.removeEventListener('board-fen-updated', handleExternalFENUpdate);
      window.removeEventListener('board-root-fen-changed', handleRootFENChanged);
      window.removeEventListener('stockfish-position-preserved', handleStockfishPositionPreserved);
      window.removeEventListener('force-board-position', handleForceBoardPosition);
      window.removeEventListener('stockfish-move-selected', handleStockfishMoveSelected);
      window.removeEventListener('stockfish-arrows-update', handleStockfishArrowsUpdate);
    };
  }, [setGame, setInitialPosition, currentFEN]);
  
  // Process any pending orientation changes safely
  useEffect(() => {
    if (pendingOrientationRef.current && dispatch && typeof dispatch === 'function') {
      const newOrientation = pendingOrientationRef.current;
      
      dispatch({ type: 'SET_ORIENTATION', payload: newOrientation });
      pendingOrientationRef.current = null;
    }
  }, [dispatch, pendingOrientationRef.current]);

  // Update local game state when global FEN changes (for move navigation)
  useEffect(() => {
    // console.log('🔥 [3Board] Board update useEffect triggered:', {
    //   currentFEN,
    //   currentGameFEN: game?.fen(),
    //   needsUpdate: currentFEN && currentFEN !== game?.fen(),
    //   currentMove,
    //   source: 'board-update-useEffect'
    // });
    
    if (currentFEN && currentFEN !== game?.fen()) {
      // console.log('🔥 [3Board] Updating board with new FEN:', currentFEN);
      
      try {
        const newGame = new Chess(currentFEN);
        setGame(newGame);
        
        // Update game state for check/checkmate detection
        updateGameState(newGame);
        
        // Update king positions for highlighting
        const kings = findKingPositions(currentFEN);
        setKingPositions(kings);
        
        // NOTE: Visual clearing is handled by the currentMove useEffect
        // which will be triggered when the move navigation occurs
        
        // Force update the window.currentFEN for consistency
        window.currentFEN = currentFEN;
        
        // console.log('🔥 [3Board] Board successfully updated to FEN:', currentFEN);
        
      } catch (error) {
        console.error('[Board] Failed to update game state with FEN:', currentFEN, error);
      }
    } else if (currentFEN === game?.fen()) {
      // console.log('🔥 [3Board] FEN already matches current game, no update needed');
    } else if (!currentFEN) {
      // console.log('🔥 [3Board] No currentFEN provided');
    }
  }, [currentFEN, currentMove]);

  // Listen for undo and start over operations
  useEffect(() => {
    // Create a global event listener for the undo operation
    const handleUndoEvent = () => {
      clearLastMoveRef();
    };
    
    // Listen to custom events that indicate an undo or reset operation
    window.addEventListener('chess-move-undo', handleUndoEvent);
    window.addEventListener('chess-game-reset', handleUndoEvent);
    
    return () => {
      window.removeEventListener('chess-move-undo', handleUndoEvent);
      window.removeEventListener('chess-game-reset', handleUndoEvent);
    };
  }, []);

  // Log when FEN is updated for debugging
  useEffect(() => {
    // console.log('🔥 [3Board] currentFEN changed:', {
    //   currentFEN,
    //   gameInstanceFEN: game?.fen(),
    //   areEqual: currentFEN === game?.fen(),
    //   source: 'currentFEN-useEffect'
    // });
  }, [currentFEN]);

  // Consolidated function to handle all visual elements (arrows, highlights, move squares)
  const loadAllVisuals = (moveId) => {
    // console.log('🔍 [3Board] loadAllVisuals called with moveId:', moveId, 'isCommenting:', isCommenting, 'state.startPosition:', state?.startPosition);
    
    // CRITICAL: ALWAYS clear first to prevent visual accumulation (especially important in comment mode)
    setUserArrows([]);
    setUserDefinedSquares({});
    setMoveSquares({});
    
    // ENHANCED COMMENT MODE: Additional safety clearing
    if (isCommenting) {
      // Force immediate clearing to prevent any state lag issues
      setUserArrows([]);
      setUserDefinedSquares({});
      setMoveSquares({});
    }
    
    // Comment mode specific logging for debugging
    if (isCommenting) {
      // console.log('🎯 [3Board] Comment mode: Loading visuals for move:', moveId);
      // console.log('🎯 [3Board] Comment mode: Current state.moves:', state?.moves?.length || 0, 'moves');
      // console.log('🎯 [3Board] Comment mode: Pre-clear visual state - arrows:', userArrows.length, 'squares:', Object.keys(userDefinedSquares).length);
    }
    
    if (moveId === '-1') {
      // ENHANCED: Load start position visuals
      if (state?.startPosition) {
        // Load start position arrows with format conversion
        if (state.startPosition.arrows && Array.isArray(state.startPosition.arrows)) {
          // console.log('🔍 [3Board] Loading startPosition arrows:', state.startPosition.arrows);
          
          // Convert object format arrows to array format for the board
          const convertedStartArrows = state.startPosition.arrows.map((arrow, index) => {
            // console.log(`🔍 [3Board] Converting arrow ${index}:`, arrow, 'type:', typeof arrow, 'isArray:', Array.isArray(arrow));
            
            // If arrow is already in array format, use it as-is
            if (Array.isArray(arrow) && arrow.length >= 3) {
              // console.log('✅ [3Board] Arrow already in array format:', arrow);
              return arrow;
            }
            // If arrow is in object format, convert to array format
            if (arrow && typeof arrow === 'object' && arrow.from && arrow.to) {
              // Handle different possible color field names
              const color = arrow.color || arrow.Color || arrow.backgroundColor || '#ff0050';
              const converted = [arrow.from, arrow.to, color];
              // console.log('✅ [3Board] Converted object arrow to array:', arrow, '->', converted);
              return converted;
            }
            // Try to handle malformed array format
            if (Array.isArray(arrow) && arrow.length === 2) {
              const converted = [arrow[0], arrow[1], '#ff0050']; // Default color
              // console.log('⚠️ [3Board] Fixed malformed array arrow (added color):', arrow, '->', converted);
              return converted;
            }
            // Invalid arrow format, skip it
            // console.log('❌ [3Board] Invalid arrow format, skipping:', arrow);
            return null;
          }).filter(arrow => arrow !== null);
          
          // console.log('🔍 [3Board] Final converted startPosition arrows:', convertedStartArrows);
          setUserArrows(convertedStartArrows);
        } else {
          // console.log('❌ [3Board] No startPosition arrows to load or invalid format:', state.startPosition?.arrows);
        }
        
        // Load start position highlighted squares
        if (state.startPosition.highlightedSquares && typeof state.startPosition.highlightedSquares === 'object') {
          setUserDefinedSquares(state.startPosition.highlightedSquares);
        }
      }
      return;
    }
    
    if (moveId === undefined) {
      // Completely undefined, visuals are already cleared above
      return;
    }
    
    try {
      // Use the state object directly from useGameLogic hook
      if (!state || !state.moves || state.moves.length === 0) {
        
        return;
      }
      
      // Find the move data for this moveId
      let moveData = null;
      
      if (!moveId.includes('|')) {
        // Main line move
        const idx = parseInt(moveId, 10);
        if (idx >= 0 && idx < state.moves.length) {
          moveData = state.moves[idx];
        }
      } else {
        // Variation move - use getNodeAtPath helper from UseGameState.js
        const parts = moveId.split('|');
        
        // Base case: Simple variation with one level (e.g., "0|0:1" or "-1|0:1")
        if (parts.length === 2) {
          const mainIdx = parseInt(parts[0], 10);
          const [varIdx, moveIdx] = parts[1].split(':').map(Number);
          
          // ENHANCED: Handle start position variations
          if (mainIdx === -1) {
            // Start position variation
            if (state?.startPosition?.variations &&
                varIdx >= 0 && varIdx < state.startPosition.variations.length &&
                moveIdx >= 0 && moveIdx < state.startPosition.variations[varIdx].length) {
              moveData = state.startPosition.variations[varIdx][moveIdx];
            }
          } else {
            // Regular main line variation
            if (mainIdx >= 0 && mainIdx < state.moves.length) {
              const mainMove = state.moves[mainIdx];
              
              // Get the variation
              if (mainMove.variations && 
                  varIdx >= 0 && varIdx < mainMove.variations.length &&
                  moveIdx >= 0 && moveIdx < mainMove.variations[varIdx].length) {
                moveData = mainMove.variations[varIdx][moveIdx];
              }
            }
          }
        } 
        // Complex case: Multiple levels of variations (e.g., "0|0:1|1:2")
        else if (parts.length > 2) {
          let currentNode = null;
          let mainIdx = parseInt(parts[0], 10);
          
          // Start with main line move
          if (mainIdx >= 0 && mainIdx < state.moves.length) {
            currentNode = state.moves[mainIdx];
            
            // Traverse the variation path
            for (let i = 1; i < parts.length; i++) {
              if (!parts[i].includes(':')) continue;
              
              const [varIdx, moveIdx] = parts[i].split(':').map(Number);
              
              // For all except the last segment, navigate to the node
              if (i < parts.length - 1) {
                if (!currentNode.variations || 
                    varIdx >= currentNode.variations.length ||
                    moveIdx >= currentNode.variations[varIdx].length) {
                  console.error('[Board] Invalid variation path at segment:', parts[i]);
                  break;
                }
                
                currentNode = currentNode.variations[varIdx][moveIdx];
              } 
              // Last segment - get the actual move data
              else {
                if (currentNode.variations && 
                    varIdx < currentNode.variations.length &&
                    moveIdx < currentNode.variations[varIdx].length) {
                  moveData = currentNode.variations[varIdx][moveIdx];
                }
              }
            }
          }
        }
      }
      
      // Load all visual elements from move data
      if (moveData) {
        
        
        // Load move highlight squares (from/to squares)
        if (moveData.from && moveData.to) {
          const highlightSquares = {
            [moveData.from]: currentTheme.moveFrom || DEFAULT_MOVE_HIGHLIGHT.moveFrom,
            [moveData.to]: currentTheme.moveTo || DEFAULT_MOVE_HIGHLIGHT.moveTo
          };
          
          setMoveSquares(highlightSquares);
        }
        
        // Load user-defined arrows
        if (moveData.arrows && Array.isArray(moveData.arrows)) {
          if (isCommenting) {
            // console.log('🎯 [3Board] Comment mode: Loading arrows for moveId:', moveId, 'arrows:', moveData.arrows);
          }
          
          // Convert object format arrows to array format for the board
          const convertedArrows = moveData.arrows.map(arrow => {
            // If arrow is already in array format, use it as-is
            if (Array.isArray(arrow)) {
              return arrow;
            }
            // If arrow is in object format, convert to array format
            if (arrow && typeof arrow === 'object' && arrow.from && arrow.to) {
              return [arrow.from, arrow.to, arrow.color || 'green'];
            }
            // Invalid arrow format, skip it
            return null;
          }).filter(arrow => arrow !== null);
          
          if (isCommenting) {
            // console.log('🎯 [3Board] Comment mode: Setting arrows:', convertedArrows);
          }
          setUserArrows(convertedArrows);
        } else if (isCommenting) {
          // console.log('🎯 [3Board] Comment mode: No arrows found for moveId:', moveId, 'ensuring arrows are cleared');
          // Explicit clearing in comment mode to prevent accumulation
          setUserArrows([]);
        }
        
        // Load user-defined highlighted squares
        if (moveData.highlightedSquares && typeof moveData.highlightedSquares === 'object') {
          if (isCommenting) {
            // console.log('🎯 [3Board] Comment mode: Loading highlighted squares for moveId:', moveId, 'squares:', moveData.highlightedSquares);
          }
          setUserDefinedSquares(moveData.highlightedSquares);
        } else if (isCommenting) {
          // console.log('🎯 [3Board] Comment mode: No highlighted squares found for moveId:', moveId, 'ensuring squares are cleared');
          // Explicit clearing in comment mode to prevent accumulation
          setUserDefinedSquares({});
        }
      } else {
        
        if (isCommenting) {
          // console.log('🎯 [3Board] Comment mode: No move data found for moveId:', moveId, 'ensuring all visuals are cleared');
          // Explicit clearing when no move data exists
          setUserArrows([]);
          setUserDefinedSquares({});
        }
      }
    } catch (error) {
      console.error('[Board] Error loading visuals:', error);
      // Visuals are already cleared at the start of this function
      // But ensure they're cleared in case of error in comment mode
      if (isCommenting) {
        setUserArrows([]);
        setUserDefinedSquares({});
        setMoveSquares({});
      }
    }
    
    // ENHANCED COMMENT MODE: Ensure comprehensive clearing
    // This is a safety net to guarantee visuals are cleared if no valid data was loaded
    if (isCommenting) {
      // Force a final validation that clearing actually worked
      setTimeout(() => {
        // Check if visuals are still present when they shouldn't be
        const currentArrows = userArrows.length;
        const currentSquares = Object.keys(userDefinedSquares).length;
        
        // If we have visuals but no saved data for this move, something went wrong
        if ((currentArrows > 0 || currentSquares > 0)) {
          // console.log('🎯 [3Board] Comment mode: Final safety check - ensuring visuals cleared for moveId:', moveId);
          
          // Double-check if this move should actually have visuals
          const shouldHaveVisuals = checkIfMoveHasVisuals(moveId);
          
          if (!shouldHaveVisuals) {
            // console.log('🔄 [3Board] Comment mode: Force clearing persisting visuals');
            setUserArrows([]);
            setUserDefinedSquares({});
          }
        }
      }, 0);
      
      // console.log('🎯 [3Board] Comment mode: Post-load visual state - arrows:', userArrows.length, 'squares:', Object.keys(userDefinedSquares).length);
    }
  };

  // Helper function to check if a move should have visual data
  const checkIfMoveHasVisuals = (moveId) => {
    if (!moveId || moveId === '-1') {
      return !!(state?.startPosition?.arrows?.length || Object.keys(state?.startPosition?.highlightedSquares || {}).length);
    }

    try {
      // Get move data using the same logic as loadAllVisuals
      let moveData = null;
      
      if (!moveId.includes('|')) {
        // Main line move
        const idx = parseInt(moveId, 10);
        if (idx >= 0 && idx < state.moves.length) {
          moveData = state.moves[idx];
        }
      } else {
        // Variation move - simplified check
        const parts = moveId.split('|');
        if (parts.length >= 2) {
          const mainIdx = parseInt(parts[0], 10);
          if (mainIdx >= 0 && mainIdx < state.moves.length) {
            // Navigate through variation path to find the move data
            // This is a simplified version - for full implementation we'd need the complete navigation logic
            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
            if (parts[0] === '-1' && state?.startPosition?.variations?.[varIdx]?.[moveIdx]) {
              moveData = state.startPosition.variations[varIdx][moveIdx];
            } else if (state.moves[mainIdx]?.variations?.[varIdx]?.[moveIdx]) {
              moveData = state.moves[mainIdx].variations[varIdx][moveIdx];
            }
          }
        }
      }
      
      return !!(moveData?.arrows?.length || Object.keys(moveData?.highlightedSquares || {}).length);
    } catch (error) {
      console.error('[3Board] Error checking move visuals:', error);
      return false;
    }
  };

  // Function to save current board visual state to a move in comment mode
  const saveCurrentVisualsToMove = (moveId) => {
    // Only save in comment mode to prevent interfering with normal gameplay
    if (!isCommenting || !moveId || moveId === '-1') {
      return;
    }

    try {
      // Capture current visual state
      const currentArrows = [...userArrows]; // Copy current arrows
      const currentSquares = { ...userDefinedSquares }; // Copy current highlighted squares

      // Only dispatch if we have visuals to save
      if (currentArrows.length > 0 || Object.keys(currentSquares).length > 0) {
        // console.log('🎯 [3Board] Comment mode: Saving visuals to move:', moveId, {
        //   arrows: currentArrows.length,
        //   squares: Object.keys(currentSquares).length
        // });

        // Convert arrows to the proper format for storage
        const arrowsForStorage = currentArrows.map(arrow => {
          // If arrow is already in array format [from, to, color], convert to object format for storage
          if (Array.isArray(arrow) && arrow.length >= 3) {
            return {
              from: arrow[0],
              to: arrow[1],
              color: arrow[2]
            };
          }
          // If already in object format, keep as-is
          return arrow;
        });

        // Dispatch action to save visual data to the move
        dispatch({
          type: 'UPDATE_MOVE_PROPERTIES_AT_PATH',
          payload: {
            path: moveId,
            properties: {
              arrows: arrowsForStorage,
              highlightedSquares: currentSquares
            }
          }
        });
      }
    } catch (error) {
      console.error('[3Board] Error saving visuals to move:', moveId, error);
    }
  };

  // DEPRECATED: This function is now replaced by loadAllVisuals
  // Keeping for backward compatibility but it now just calls the new function
  const loadMoveVisuals = (moveId) => {
    loadAllVisuals(moveId);
  };

  // DEPRECATED: This function is now replaced by loadAllVisuals  
  // Keeping for backward compatibility but it now just calls the new function
  const updateMoveHighlightSquares = (index) => {
    loadAllVisuals(index);
  };

  // Single useEffect to handle all visual updates when current move changes
  useEffect(() => {
    if (currentMove !== previousMoveRef.current) {
      
      // COMMENT MODE: Save current visuals to the previous move before navigation
      if (isCommenting && previousMoveRef.current !== null) {
        saveCurrentVisualsToMove(previousMoveRef.current);
      }
      
      previousMoveRef.current = currentMove;
      
      // Clear click-to-move selection states when navigating moves
      setMoveFrom('');
      setMoveTo(null);
      setOptionSquares({});
      
      // Add debug logging for variations
      if (currentMove && currentMove.includes('|')) {
        
      }
      
      // Load all visuals for the current move (this function clears first, then loads)
      loadAllVisuals(currentMove);
    }
  }, [currentMove, state]);

  // Update move highlight squares when board theme changes
  useEffect(() => {
    if (currentMove !== '-1' && Object.keys(moveSquares).length > 0) {
      // Re-apply the current move highlight with new theme colors
      loadAllVisuals(currentMove);
    }
  }, [boardTheme, currentTheme]);

  // Handle piece movement
  const onDrop = (sourceSquare, targetSquare) => {
    
    
    try {
      // PUZZLE MODE: Check if this move would create a NEW variation and prevent it
      // Using isPuzzleMode to avoid shadowing the isPuzzle prop
      const isPuzzleMode = (typeof window !== 'undefined' && window.isPuzzleMode) || isPuzzle || false;
      
      if (isPuzzleMode) {
        // First check if we would be creating a variation (not at end of line)
        const notAtEndOfLine = (currentMove !== '-1' && 
                               !currentMove.includes('|') && 
                               parseInt(currentMove, 10) < moves.length - 1) ||
                              (currentMove === '-1' && moves.length > 0);
        
        if (notAtEndOfLine) {
          // Create the move to get the SAN notation for comparison
          const gameCopy = new Chess(game.fen());
          const testMove = gameCopy.move({
            from: sourceSquare,
            to: targetSquare,
            promotion: 'q'
          });
          
          if (!testMove) {
            // Invalid move anyway, let normal logic handle it
            return false;
          }
          
          // Check if this move matches the existing next move in the line
          const nextMove = getNextMove();
          const isPlayingExistingMove = nextMove && nextMove.move.notation === testMove.san;
          
          if (!isPlayingExistingMove) {
            // console.log('🔒 [3Board] Puzzle mode: Incorrect move -', testMove.san, 'Expected:', nextMove?.move.notation);
            // Don't return false here! Let the validation function handle it so the popup can show
            // The validation function below will prevent the move AND show the popup
          } else {
            // console.log('✅ [3Board] Puzzle mode: Allowing existing move -', testMove.san);
          }
        }
      }
      
      // IMMEDIATELY clear all visual elements to prevent stale visuals
      setUserArrows([]);
      setUserDefinedSquares({});
      setMoveSquares({});
      
      // Create a new game instance to avoid modifying state directly
      const gameCopy = new Chess(game.fen());
      
      // Make the move
      const move = gameCopy.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q' // Always promote to queen for simplicity
      });
      
      
      
      // If move is illegal, return false to prevent the move
      if (!move) {
        
        // Reload visuals for current position since we cleared them
        loadAllVisuals(currentMove);
        return false;
      }
      
      // Call new move detection callback if provided
      if (onNewMove && typeof onNewMove === 'function') {
        onNewMove(move.san, sourceSquare, targetSquare, 'user');
      }
      
      // Check if this move already exists at the next position OR as a variation
      const nextMove = getNextMove();
      
      // First check the next move in the current line
      if (nextMove && nextMove.move.notation === move.san) {
        // console.log('Move matches next move in current line:', nextMove.id);
        goToMove(nextMove.id);
        
        // Update local game state to match
        setGame(gameCopy);
        
        // Highlight the move squares immediately
        const highlightSquares = {
          [sourceSquare]: currentTheme.moveFrom || DEFAULT_MOVE_HIGHLIGHT.moveFrom,
          [targetSquare]: currentTheme.moveTo || DEFAULT_MOVE_HIGHLIGHT.moveTo
        };
        setMoveSquares(highlightSquares);
        
        return true;
      }
      
      // If not the next move, check for variations from current position
      let variationMatch = null;
      
      if (currentMove === '-1') {
        // Check start position variations
        if (gameState?.startPosition?.variations) {
          gameState.startPosition.variations.forEach((variation, varIndex) => {
            if (variation && variation.length > 0 && variation[0].notation === move.san) {
              variationMatch = { path: `-1|${varIndex}:0`, notation: move.san };
            }
          });
        }
      } else if (!currentMove.includes('|')) {
        // We're on main line - check variations from this move
        const currentIndex = parseInt(currentMove, 10);
        if (!isNaN(currentIndex) && moves[currentIndex]?.variations) {
          moves[currentIndex].variations.forEach((variation, varIndex) => {
            if (variation && variation.length > 0 && variation[0].notation === move.san) {
              variationMatch = { path: `${currentIndex}|${varIndex}:0`, notation: move.san };
            }
          });
        }
      } else {
        // We're in a variation - check for nested variations
        // This is more complex, let it fall through to onNewMove for now
      }
      
      if (variationMatch) {
        // console.log('Move matches variation:', variationMatch.path);
        goToMove(variationMatch.path);
        
        // Update local game state to match
        setGame(gameCopy);
        
        // Highlight the move squares immediately
        const highlightSquares = {
          [sourceSquare]: currentTheme.moveFrom || DEFAULT_MOVE_HIGHLIGHT.moveFrom,
          [targetSquare]: currentTheme.moveTo || DEFAULT_MOVE_HIGHLIGHT.moveTo
        };
        setMoveSquares(highlightSquares);
        
        return true;
      }
      
      
      
      // Puzzle move validation - check if move is correct before allowing it
      if (isPuzzle && onValidateMove) {
        
        const isValidPuzzleMove = onValidateMove(move);
        if (!isValidPuzzleMove) {
          
          // Reload visuals for current position since we cleared them
          loadAllVisuals(currentMove);
          return false; // Prevent the move from being recorded
        }
        
      } else {
        
      }
      
      // Starting variation validation - only in AddGame mode (!isViewMode)
      if (!isViewMode) {
        // Check if we're creating a starting variation (from starting position)
        if (currentMove === '-1') {
          // Check if we already have a starting variation
          if (state?.startPosition?.variations && state.startPosition.variations.length >= 1) {
            // console.log('[Board] Preventing second starting variation - limit reached');
            // Trigger the modal callback
            if (onStartingVariationLimit) {
              onStartingVariationLimit();
            }
            // Reload visuals for current position since we cleared them
            loadAllVisuals(currentMove);
            return false; // Prevent the move
          }
        }
        
        // Check if we're trying to create a SECOND starting variation LINE
        // Move IDs like -1|1:0 indicate the second variation line (index 1)
        if (currentMove.startsWith('-1|')) {
          const parts = currentMove.split('|');
          if (parts.length >= 2) {
            const variationPart = parts[1];
            const [varIdx] = variationPart.split(':').map(Number);
            
            // If trying to create variation line 1 (second variation) or higher, prevent it
            if (varIdx >= 1) {
              // console.log('[Board] Preventing second starting variation line - limit reached, varIdx:', varIdx);
              // Trigger the modal callback
              if (onStartingVariationLimit) {
                onStartingVariationLimit();
              }
              // Reload visuals for current position since we cleared them
              loadAllVisuals(currentMove);
              return false; // Prevent the move
            }
            
            // Allow moves within the first variation (varIdx === 0)
            // console.log('[Board] Allowing move within first starting variation:', currentMove);
          }
        }
      }
      
      // Only track non-duplicate moves in normal gameplay
      if (move.san === lastMoveRef.current) {
        
        return true;
      }
      
      lastMoveRef.current = move.san;
      
      // First update the local game state to ensure proper rendering
      setGame(gameCopy);

      // Highlight the source and target squares with our custom highlight color
      const highlightSquares = {
        [sourceSquare]: currentTheme.moveFrom || DEFAULT_MOVE_HIGHLIGHT.moveFrom,
        [targetSquare]: currentTheme.moveTo || DEFAULT_MOVE_HIGHLIGHT.moveTo
      };
      setMoveSquares(highlightSquares);
      
      // Get the new FEN position after the move
      const newFEN = gameCopy.fen();
      
      // Update game state with move notation for check/checkmate detection
      updateGameState(gameCopy, move.san);
      
      // Call the global FEN handler to trigger opening detection
      if (window.addGameHandleFenChange && typeof window.addGameHandleFenChange === 'function') {
        // 
        window.addGameHandleFenChange(newFEN);
      }
      
      // Also call the ViewGame handler if it exists
      if (window.viewGameHandleFenChange && typeof window.viewGameHandleFenChange === 'function') {
        
        window.viewGameHandleFenChange(newFEN);
      }
      
      // Dispatch board-fen-updated event for move changes
      
      window.dispatchEvent(new CustomEvent('board-fen-updated', { 
        detail: { 
          fen: newFEN,
          source: 'board-component-move',
          move: move.san,
          timestamp: Date.now() 
        } 
      }));
      
      // Trigger Stockfish analysis for the new position
      triggerStockfishAnalysis(newFEN);
      
      // NOTE: Visual elements will be cleared and reloaded by the currentMove useEffect
      // when the move is recorded and currentMove changes
      
      // Prepare additional data for the move with all required fields
      // NOTE: We capture empty arrays since visuals will be managed by the move navigation system
      const additionalData = { 
        fen: newFEN,
        startSquare: sourceSquare,
        endSquare: targetSquare,
        arrows: [], // Empty - visuals managed by navigation system
        highlightedSquares: {}, // Empty - visuals managed by navigation system
        content: "", // Initialize empty content for later editing
        isViewMode: isViewMode // Pass ViewMode information for move handling
      };
      
      // Add comment metadata if in commenting mode
      if (isCommenting) {
        additionalData.creator = {
          id: -1, // Temporary ID for unsaved comments
          type: CHESSBOARD.MOVE_TYPES.COMMENT
        };
        
      } else {
        
      }
      
      // Record the move in global state 
      recordMove(move.san, sourceSquare, targetSquare, additionalData);
      
      // Update the current FEN global for reference
      window.currentFEN = newFEN;
      
      
      
      // After recording a move, manually check for opening information after a small delay
      // to ensure state is updated
      setTimeout(() => {
        if (window.addGameHandleFenChange) {
          
          window.addGameHandleFenChange(newFEN);
        }
      }, 300);
      
      return true;
    } catch (e) {
      console.error('Error making move:', e);
      return false;
    }
  };

  // Process the FEN to ensure it's valid
  const processedFEN = () => {
    // First priority: use the local game state for most responsive UI
    return game.fen();
  };

  // Get legal moves for a square and set up visual indicators
  const getMoveOptions = (square) => {
    // Check if there's a piece on this square
    if (!game.get(square)) return false;
    
    // Always highlight the selected square
    const newSquares = {
      [square]: SELECTED_SQUARE_COLOR
    };
    
    // Only add legal move circles when showLegalMoves is enabled
    if (showLegalMoves) {
      const moves = game.moves({
        square,
        verbose: true
      });
      
      if (moves.length > 0) {
        moves.forEach(move => {
          // Check if target square has an enemy piece (capture) or is empty
          const targetPiece = game.get(move.to);
          const isCapture = targetPiece && targetPiece.color !== game.get(square).color;
          
          newSquares[move.to] = {
            background: isCapture ? LEGAL_MOVE_CIRCLE_CAPTURE : LEGAL_MOVE_CIRCLE_NORMAL,
            borderRadius: '50%'
          };
        });
      }
    }
    
    setOptionSquares(newSquares);
    return true; // Always return true if a piece is at the square
  };

  // Handle arrow drawing
  const onArrowsChange = (rawArrows) => {
    if (rawArrows.length === 0) {
      return;
    }
    
    // Convert the arrows to our format with the active color
    const arrows = rawArrows.map(arrow => ([arrow[0], arrow[1], activeColor]));
    
    // Add the arrows to the user arrows state
    setUserArrows(prevArrows => {
      // Safety check to ensure prevArrows is an array
      const safeArrows = Array.isArray(prevArrows) ? prevArrows : [];
      const newArrows = [...safeArrows, ...arrows];
      
      // ENHANCED: Update move properties including start position variations
      // console.log('🎯 [3Board] Arrow drawing - currentMove:', currentMove, 'newArrows:', newArrows);
      if (currentMove !== '-1') {
        
        
        // Handle start position variations vs regular moves
        if (currentMove.startsWith('-1|')) {
          // This is a start position variation - use the specialized action
          // console.log('🔥 [3Board] Dispatching UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS for arrows');
          if (dispatch && typeof dispatch === 'function') {
            // console.log('🚀 [3Board] About to dispatch for arrows:', { 
            //   type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
            //   moveId: currentMove,
            //   arrows: newArrows 
            // });
            // console.log('🔍 [3Board] dispatch function:', dispatch, typeof dispatch);
            // console.log('🔍 [3Board] dispatch function source:', dispatch.toString().slice(0, 200));
            // console.log('🔍 [3Board] About to call dispatch directly');
            const dispatchResult = dispatch({ 
              type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
              payload: { 
                moveId: currentMove,
                properties: { arrows: newArrows }
              }
            });
            // console.log('🔍 [3Board] Dispatch result:', dispatchResult);
            // console.log('✅ [3Board] Arrow dispatch completed');
          } else {
            console.error('❌ [3Board] dispatch is not available:', dispatch);
          }
        } else {
          // Regular move - use the standard action
          dispatch({ 
            type: 'UPDATE_MOVE_PROPERTIES', 
            payload: { 
              moveId: currentMove,
              properties: { arrows: newArrows }
            }
          });
        }
      } else {
        // Handle pure start position (currentMove === '-1')
        // console.log('🔥 [3Board] Dispatching UPDATE_START_POSITION_VISUALS (arrows)', {
        //   currentMove,
        //   newArrows,
        //   newArrowsLength: newArrows?.length,
        //   existingHighlights: state.startPosition?.highlightedSquares,
        //   hasStartPosition: !!state.startPosition,
        //   stateKeys: state ? Object.keys(state) : 'no state'
        // });
        
        // Ensure we have safe fallbacks for existing data
        const safeHighlights = (state.startPosition && state.startPosition.highlightedSquares) ? 
          state.startPosition.highlightedSquares : {};
        
        dispatch({
          type: 'UPDATE_START_POSITION_VISUALS',
          payload: { 
            arrows: newArrows,
            highlightedSquares: safeHighlights
          }
        });
      }
      
      return newArrows;
    });
  };

  // Handle square click (for both click-to-move and arrow removal)
  const handleSquareClick = (square) => {
    // Mobile diagram mode - handle square highlighting on left click
    if (mobileActiveColor && !moveFrom) {
      // Toggle highlight using the mobile active color
      setUserDefinedSquares(prevSquares => {
        const safeSquares = (prevSquares && typeof prevSquares === 'object') ? prevSquares : {};
        const newSquares = { ...safeSquares };
        
        if (newSquares[square]) {
          delete newSquares[square];
        } else {
          newSquares[square] = { backgroundColor: mobileActiveColor };
        }
        
        // Dispatch to save state
        setTimeout(() => {
          if (currentMove !== '-1') {
            if (currentMove.startsWith('-1|')) {
              dispatch({ 
                type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
                payload: { 
                  moveId: currentMove,
                  properties: { highlightedSquares: newSquares }
                }
              });
            } else {
              dispatch({ 
                type: 'UPDATE_MOVE_PROPERTIES', 
                payload: { 
                  moveId: currentMove,
                  properties: { highlightedSquares: newSquares }
                }
              });
            }
          } else {
            // console.log('🔥 [3Board] Dispatching UPDATE_START_POSITION_VISUALS (mobile squares)', {
            //   currentMove,
            //   newSquares,
            //   existingArrows: state.startPosition?.arrows,
            //   hasStartPosition: !!state.startPosition
            // });
            
            // Ensure we have safe fallbacks for existing data
            const safeArrows = (state.startPosition && Array.isArray(state.startPosition.arrows)) ? 
              state.startPosition.arrows : [];
            
            dispatch({
              type: 'UPDATE_START_POSITION_VISUALS',
              payload: { 
                highlightedSquares: newSquares,
                arrows: safeArrows
              }
            });
          }
        }, 0);
        
        return newSquares;
      });
      return; // Don't process normal click-to-move
    }

    // Mobile arrow mode - handle arrow drawing on left click
    if (mobileArrowMode && mobileArrowColor && onMobileArrowStart) {
      if (!mobileArrowStart) {
        // First click - set start square
        onMobileArrowStart(square);
        return; // Don't process click-to-move
      } else {
        // Second click - draw arrow (unless clicking same square)
        if (mobileArrowStart !== square) {
          const newArrow = [mobileArrowStart, square, mobileArrowColor];
          
          // Add arrow to visual state immediately
          setUserArrows(prevArrows => {
            const safeArrows = Array.isArray(prevArrows) ? prevArrows : [];
            const newArrows = [...safeArrows, newArrow];
            
            // Dispatch to save state
            setTimeout(() => {
              if (currentMove !== '-1') {
                if (currentMove.startsWith('-1|')) {
                  dispatch({ 
                    type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
                    payload: { 
                      moveId: currentMove,
                      properties: { arrows: newArrows }
                    }
                  });
                } else {
                  dispatch({ 
                    type: 'UPDATE_MOVE_PROPERTIES', 
                    payload: { 
                      moveId: currentMove,
                      properties: { arrows: newArrows }
                    }
                  });
                }
              } else {
                // console.log('🔥 [3Board] Dispatching UPDATE_START_POSITION_VISUALS (mobile arrows)', {
                //   currentMove,
                //   newArrows,
                //   newArrowsType: typeof newArrows,
                //   newArrowsLength: newArrows?.length,
                //   firstArrow: newArrows?.[0],
                //   existingHighlights: state.startPosition?.highlightedSquares,
                //   hasStartPosition: !!state.startPosition
                // });
                
                // Ensure we have safe fallbacks for existing data
                const safeHighlights = (state.startPosition && state.startPosition.highlightedSquares) ? 
                  state.startPosition.highlightedSquares : {};
                
                dispatch({
                  type: 'UPDATE_START_POSITION_VISUALS',
                  payload: { 
                    arrows: newArrows,
                    highlightedSquares: safeHighlights
                  }
                });
              }
            }, 0);
            
            return newArrows;
          });
        }
        
        // Clear arrow start
        onMobileArrowStart(null);
        return; // Don't process click-to-move
      }
    }

    // First handle click-to-move logic
    // Clear option squares when any square is clicked (unless selecting a piece)
    if (Object.keys(optionSquares).length > 0 && !optionSquares[square]) {
      setOptionSquares({});
    }
    
    // From square selection (first click)
    if (!moveFrom) {
      const hasMoveOptions = getMoveOptions(square);
      if (hasMoveOptions) {
        setMoveFrom(square);
        return; // Don't process arrow removal if we selected a piece
      }
    } else {
      // Check if clicking the same piece again (deselect)
      if (moveFrom === square) {
        setMoveFrom('');
        setOptionSquares({});
        return; // Don't process arrow removal
      }
      
      // To square selection (second click)
      if (!moveTo) {
        // Check if this is a valid move
        const moves = game.moves({
          verbose: true
        });
        const foundMove = moves.find(m => m.from === moveFrom && m.to === square);
        
        // Not a valid move
        if (!foundMove) {
          // Check if clicked on a new piece
          const hasMoveOptions = getMoveOptions(square);
          // If new piece, set it as moveFrom, otherwise clear selection
          setMoveFrom(hasMoveOptions ? square : '');
          return; // Don't process arrow removal
        }
        
        // Valid move - execute it using existing onDrop logic
        setMoveTo(square);
        
        // Clear selection states
        setMoveFrom('');
        setMoveTo(null);
        setOptionSquares({});
        
        // Execute the move using existing drag-and-drop logic
        const moveSuccess = onDrop(moveFrom, square);
        
        if (moveSuccess) {
          return; // Don't process arrow removal if move was successful
        }
      }
    }
    
    // If we get here, handle arrow removal (existing functionality)
    setUserArrows(prevArrows => {
      // Safety check to ensure prevArrows is an array
      const safeArrows = Array.isArray(prevArrows) ? prevArrows : [];
      const filteredArrows = safeArrows.filter(([from, to]) => from !== square && to !== square);
      
      // ENHANCED: Update move properties including start position variations
      if (currentMove !== '-1' && filteredArrows.length !== safeArrows.length) {
        
        
        // Handle start position variations vs regular moves
        if (currentMove.startsWith('-1|')) {
          // This is a start position variation - use the specialized action
          dispatch({ 
            type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
            payload: { 
              moveId: currentMove,
              properties: { arrows: filteredArrows }
            }
          });
        } else {
          // Regular move - use the standard action
          dispatch({ 
            type: 'UPDATE_MOVE_PROPERTIES', 
            payload: { 
              moveId: currentMove,
              properties: { arrows: filteredArrows }
            }
          });
        }
      } else if (currentMove === '-1' && filteredArrows.length !== safeArrows.length) {
        // Handle pure start position arrow removal
        dispatch({
          type: 'UPDATE_START_POSITION_VISUALS',
          payload: { 
            arrows: filteredArrows,
            highlightedSquares: state.startPosition?.highlightedSquares || {}
          }
        });
      }
      
      return filteredArrows;
    });
  };

  // Handle square right click (to add/remove highlights)
  const handleSquareRightClick = (square) => {
    // ENHANCED: Allow highlighting for all positions including start position
    
    // Calculate new squares first
    setUserDefinedSquares(prevSquares => {
      // Safety check to ensure prevSquares is an object
      const safeSquares = (prevSquares && typeof prevSquares === 'object') ? prevSquares : {};
      
      // Create a new object to avoid mutation
      const newSquares = { ...safeSquares };
      
      // Toggle highlight
      if (newSquares[square]) {
        delete newSquares[square];
      } else {
        newSquares[square] = { backgroundColor: activeColor };
      }
      
      // ENHANCED: Handle all move types including start position variations
      // console.log('🎯 [3Board] Square highlighting - currentMove:', currentMove, 'newSquares:', newSquares);
      
      // Dispatch after state calculation (using setTimeout to ensure state update happens first)
      setTimeout(() => {
        if (currentMove !== '-1') {
          // Handle start position variations vs regular moves
          if (currentMove.startsWith('-1|')) {
            // This is a start position variation - use the specialized action
            // console.log('🔥 [3Board] Dispatching UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS for squares');
            if (dispatch && typeof dispatch === 'function') {
              // console.log('🚀 [3Board] About to dispatch for squares:', { 
              //   type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
              //   moveId: currentMove,
              //   squares: newSquares 
              // });
              // console.log('🔍 [3Board] dispatch function:', dispatch, typeof dispatch);
              // console.log('🔍 [3Board] About to call dispatch directly for squares');
              const dispatchResult = dispatch({ 
                type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
                payload: { 
                  moveId: currentMove,
                  properties: { highlightedSquares: newSquares }
                }
              });
              // console.log('🔍 [3Board] Squares dispatch result:', dispatchResult);
              // console.log('✅ [3Board] Square dispatch completed');
            } else {
              console.error('❌ [3Board] dispatch is not available:', dispatch);
            }
          } else {
            // Regular move - use the standard action
            dispatch({ 
              type: 'UPDATE_MOVE_PROPERTIES', 
              payload: { 
                moveId: currentMove,
                properties: { highlightedSquares: newSquares }
              }
            });
          }
        } else {
          // Handle pure start position highlighting
          dispatch({
            type: 'UPDATE_START_POSITION_VISUALS',
            payload: { 
              highlightedSquares: newSquares,
              arrows: state.startPosition?.arrows || []
            }
          });
        }
      }, 0);
      
      return newSquares;
    });
  };

  // Function to build custom styles including user-defined highlights
  const buildCustomStyles = () => {
    // Start with empty styles object
    let styles = {};
    
    // Add move highlights first
    if (moveSquares && typeof moveSquares === 'object') {
      styles = { ...moveSquares };
    }
    
    // Add click-to-move option squares (selected piece and legal moves)
    if (optionSquares && typeof optionSquares === 'object') {
      Object.entries(optionSquares).forEach(([square, style]) => {
        if (style && typeof style === 'object') {
          // Merge with existing styles, giving priority to option squares
          styles[square] = { ...styles[square], ...style };
        }
      });
    }
    
    // Add king background colors based on game state
    if (kingPositions.white && kingPositions.black) {
      // Handle checkmate scenario - green for winning king, red for losing king
      if (gameState.isCheckmate && gameState.winner) {
        const winningKing = kingPositions[gameState.winner];
        const losingKing = kingPositions[gameState.winner === 'white' ? 'black' : 'white'];

        // Only apply if user hasn't manually highlighted these squares
        if (!userDefinedSquares[winningKing]) {
          styles[winningKing] = {
            ...styles[winningKing],
            backgroundColor: 'rgba(0, 128, 0, 0.5)' // Green with 50% opacity
          };
        }

        if (!userDefinedSquares[losingKing]) {
          styles[losingKing] = {
            ...styles[losingKing],
            backgroundColor: 'rgba(220, 0, 0, 0.5)' // Red with 50% opacity
          };
        }
      }
      // Handle check scenario (but not checkmate) - tan for king in check
      else if (gameState.isCheck && !gameState.isCheckmate) {
        // The king in check is the one whose turn it is in the current position
        const kingInCheckColor = game.turn() === 'w' ? 'white' : 'black';
        const kingInCheckSquare = kingPositions[kingInCheckColor];

        // Only apply if user hasn't manually highlighted this square
        if (kingInCheckSquare && !userDefinedSquares[kingInCheckSquare]) {
          styles[kingInCheckSquare] = {
            ...styles[kingInCheckSquare],
            backgroundColor: 'rgba(210, 180, 140)' // Tan with 100% opacity
          };
        }
      }
    }
    
    // Add user-defined highlights (they take precedence over automatic king highlights)
    if (userDefinedSquares && typeof userDefinedSquares === 'object') {
      Object.entries(userDefinedSquares).forEach(([square, style]) => {
        // Ensure style is a valid object
        if (style && typeof style === 'object') {
          styles[square] = { ...style }; // Replace completely, don't merge
        }
      });
    }
    
    // Add external custom square styles (they take highest precedence)
    if (externalCustomSquareStyles && typeof externalCustomSquareStyles === 'object') {
      Object.entries(externalCustomSquareStyles).forEach(([square, style]) => {
        // Ensure style is a valid object
        if (style && typeof style === 'object') {
          styles[square] = { ...styles[square], ...style }; // Merge with existing styles
        }
      });
    }
    
    return styles;
  };

  // Function to combine all arrows
  const getCombinedArrows = () => {
    // Ensure stockfishArrows and userArrows are valid arrays
    const safeStockfishArrows = Array.isArray(stockfishArrows) ? stockfishArrows : [];
    const safeUserArrows = Array.isArray(userArrows) ? userArrows : [];
    
    // Filter out any invalid arrow entries (must be arrays with at least 2 elements)
    const filteredStockfish = safeStockfishArrows.filter(arrow => 
      Array.isArray(arrow) && arrow.length >= 2 && typeof arrow[0] === 'string' && typeof arrow[1] === 'string');
    
    const filteredUser = safeUserArrows.filter(arrow => 
      Array.isArray(arrow) && arrow.length >= 2 && typeof arrow[0] === 'string' && typeof arrow[1] === 'string');
    
    return [...filteredStockfish, ...filteredUser];
  };

  // Get the current player turn
  const currentTurn = game ? game.turn() : 'w';

  // Add flip board functionality
  const toggleOrientation = () => {
    const newOrientation = orientation === 'white' ? 'black' : 'white';
    if (dispatch && typeof dispatch === 'function') {
      dispatch({ type: 'SET_ORIENTATION', payload: newOrientation });
      
    } else {
      console.error('[Board] Cannot flip orientation: dispatch is not available');
    }
  };

  // Add click handlers for player info expansion
  const handlePlayerInfoWClick = () => {
    setPlayerInfoW(prev => {
      const newExpanded = !prev.expanded;
      localStorage.setItem('playerInfoW_expanded', newExpanded.toString());
      
      // If closing this panel, also close the other panel
      if (!newExpanded) {
        setPlayerInfoB(prevB => {
          localStorage.setItem('playerInfoB_expanded', 'false');
          return { ...prevB, expanded: false };
        });
      }
      
      return { ...prev, expanded: newExpanded };
    });
  };

  const handlePlayerInfoBClick = () => {
    setPlayerInfoB(prev => {
      const newExpanded = !prev.expanded;
      localStorage.setItem('playerInfoB_expanded', newExpanded.toString());
      
      // If closing this panel, also close the other panel
      if (!newExpanded) {
        setPlayerInfoW(prevW => {
          localStorage.setItem('playerInfoW_expanded', 'false');
          return { ...prevW, expanded: false };
        });
      }
      
      return { ...prev, expanded: newExpanded };
    });
  };

  // Add text change handlers for editable mode
  const handlePlayerInfoWPlayerChange = (e) => {
    let newPlayer = e.target.value.slice(0, 20); // Max 20 characters
    
    // Filter profanity
    if (isProfane(newPlayer)) {
      // Don't update if the name contains profanity
      return;
    }
    
    setPlayerInfoW(prev => {
      const updated = { ...prev, player: newPlayer };
      // Call onPlayerDataChange if in AddGame mode
      if (!isViewMode && onPlayerDataChange) {
        onPlayerDataChange({
          oppositePlayer: [updated.player, updated.rating],
          povPlayer: [playerInfoB.player, playerInfoB.rating]
        });
      }
      return updated;
    });
    // Only persist to localStorage in ViewMode - AddGame mode should reset on refresh
    if (isViewMode) {
      localStorage.setItem('playerInfoW_player', newPlayer);
    }
  };

  const handlePlayerInfoWRatingChange = (e) => {
    const newRating = e.target.value.replace(/\D/g, '').slice(0, 4); // Only digits, max 4
    setPlayerInfoW(prev => {
      const updated = { ...prev, rating: newRating };
      // Call onPlayerDataChange if in AddGame mode
      if (!isViewMode && onPlayerDataChange) {
        onPlayerDataChange({
          oppositePlayer: [updated.player, updated.rating],
          povPlayer: [playerInfoB.player, playerInfoB.rating]
        });
      }
      return updated;
    });
    // Only persist to localStorage in ViewMode - AddGame mode should reset on refresh
    if (isViewMode) {
      localStorage.setItem('playerInfoW_rating', newRating);
    }
  };

  const handlePlayerInfoBPlayerChange = (e) => {
    let newPlayer = e.target.value.slice(0, 20); // Max 20 characters
    
    // Filter profanity
    if (isProfane(newPlayer)) {
      // Don't update if the name contains profanity
      return;
    }
    
    setPlayerInfoB(prev => {
      const updated = { ...prev, player: newPlayer };
      // Call onPlayerDataChange if in AddGame mode
      if (!isViewMode && onPlayerDataChange) {
        onPlayerDataChange({
          oppositePlayer: [playerInfoW.player, playerInfoW.rating],
          povPlayer: [updated.player, updated.rating]
        });
      }
      return updated;
    });
    // Only persist to localStorage in ViewMode - AddGame mode should reset on refresh
    if (isViewMode) {
      localStorage.setItem('playerInfoB_player', newPlayer);
    }
  };

  const handlePlayerInfoBRatingChange = (e) => {
    const newRating = e.target.value.replace(/\D/g, '').slice(0, 4); // Only digits, max 4
    setPlayerInfoB(prev => {
      const updated = { ...prev, rating: newRating };
      // Call onPlayerDataChange if in AddGame mode
      if (!isViewMode && onPlayerDataChange) {
        onPlayerDataChange({
          oppositePlayer: [playerInfoW.player, playerInfoW.rating],
          povPlayer: [updated.player, updated.rating]
        });
      }
      return updated;
    });
    // Only persist to localStorage in ViewMode - AddGame mode should reset on refresh
    if (isViewMode) {
      localStorage.setItem('playerInfoB_rating', newRating);
    }
  };

  // Add method to force open both player info panels
  const forceOpenBothPlayerPanels = () => {
    setPlayerInfoW(prev => {
      localStorage.setItem('playerInfoW_expanded', 'true');
      return { ...prev, expanded: true };
    });
    setPlayerInfoB(prev => {
      localStorage.setItem('playerInfoB_expanded', 'true');
      return { ...prev, expanded: true };
    });
  };

  // Add method to close both player info panels
  const closeBothPlayerPanels = () => {
    setPlayerInfoW(prev => {
      localStorage.setItem('playerInfoW_expanded', 'false');
      return { ...prev, expanded: false };
    });
    setPlayerInfoB(prev => {
      localStorage.setItem('playerInfoB_expanded', 'false');
      return { ...prev, expanded: false };
    });
  };

  // Add method to check if both panels are expanded
  const areBothPlayerPanelsExpanded = () => {
    return playerInfoW.expanded && playerInfoB.expanded;
  };

  // Add method to clear internal player data (for new games)
  const clearInternalPlayerData = () => {
    setPlayerInfoW({
      expanded: false,
      player: '',
      rating: ''
    });
    setPlayerInfoB({
      expanded: false,
      player: '',
      rating: ''
    });
  };

  // Function to find the positions of both kings on the board
  const findKingPositions = (fen) => {
    const chess = new Chess(fen);
    const board = chess.board();
    const positions = { white: null, black: null };

    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece && piece.type === 'k') {
          const square = String.fromCharCode(97 + file) + (8 - rank);
          positions[piece.color === 'w' ? 'white' : 'black'] = square;
        }
      }
    }

    return positions;
  };

  // Update game state based on chess position and move notation
  const updateGameState = (chess, moveNotation = '') => {
    const isCheckmate = chess.isCheckmate();
    const isCheck = chess.isCheck();
    let isDraw = chess.isDraw() || chess.isStalemate() || chess.isThreefoldRepetition() || chess.isInsufficientMaterial();

    // Also detect check/checkmate from SAN notation
    let detectedCheck = isCheck;
    let detectedCheckmate = isCheckmate;
    
    if (moveNotation) {
      // Check for check symbol (+) in move notation
      if (moveNotation.includes('+') && !moveNotation.includes('#')) {
        detectedCheck = true;
      }
      // Check for checkmate symbol (#) in move notation
      if (moveNotation.includes('#')) {
        detectedCheckmate = true;
        detectedCheck = false; // Checkmate overrides check
      }
    }

    // Determine winner in case of checkmate
    let winner = null;
    if (detectedCheckmate) {
      // If it's checkmate, the winner is opposite of the current turn
      winner = chess.turn() === 'w' ? 'black' : 'white';
    }

    setGameState({ 
      isCheckmate: detectedCheckmate, 
      isCheck: detectedCheck, 
      isDraw, 
      winner 
    });

    // Update king positions
    const positions = findKingPositions(chess.fen());
    setKingPositions(positions);

    
  };

  // Helper function to convert chess notation to coordinates
  const squareToCoordinates = (square) => {
    const file = square.charCodeAt(0) - 'a'.charCodeAt(0);
    const rank = 8 - parseInt(square[1]);
    
    // Account for board orientation
    if (orientation === 'black') {
      // When board is flipped, coordinates need to be inverted
      return {
        file: 7 - file,
        rank: 7 - rank
      };
    }
    
    return { file, rank };
  };

  // Helper function to calculate smart icon positioning based on board edges
  const getSmartIconPosition = (file, rank, squareSize, iconSize) => {
    let top, left;
    
    // Check if on top rank (rank 0 = 8th rank in chess)
    const isTopRank = rank === 0;
    // Check if on right file (file 7 = H file in chess)
    const isRightFile = file === 7;
    
    if (isTopRank && isRightFile) {
      // Top-right corner: place in bottom-left of square
      top = `${rank * squareSize + squareSize - 6}px`;
      left = `${file * squareSize - iconSize + 6}px`;
    } else if (isTopRank) {
      // Top rank (not corner): place in bottom-right of square
      top = `${rank * squareSize + squareSize - 6}px`;
      left = `${file * squareSize + (squareSize - iconSize)}px`;
    } else if (isRightFile) {
      // Right file (not corner): place in top-left of square
      top = `${rank * squareSize + -6}px`;
      left = `${file * squareSize - iconSize + 6}px`;
    } else {
      // Normal position: top-right of square
      top = `${rank * squareSize + -6}px`;
      left = `${file * squareSize + (squareSize - iconSize)}px`;
    }
    
    return { top, left };
  };

  // Function to render game state icons overlay
  const renderIconsOverlay = () => {
    const overlayElements = [];

    // Add game state icons - check/checkmate indicators
    if (kingPositions.white && kingPositions.black) {
      // Handle checkmate scenario
      if (gameState.isCheckmate && gameState.winner) {
        // Find winning king position
        const winningKingSquare = gameState.winner === 'white' ? kingPositions.white : kingPositions.black;
        const losingKingSquare = gameState.winner === 'white' ? kingPositions.black : kingPositions.white;

        if (winningKingSquare) {
          const { file: winFile, rank: winRank } = squareToCoordinates(winningKingSquare);
          const iconSize = isRecordMode ? 12 : 15;
          const { top, left } = getSmartIconPosition(winFile, winRank, squareSize, iconSize);
          
          overlayElements.push(
            <img
              key={`winner-king`}
              src={winnerIcon}
              alt="Winning king"
              style={{
                position: 'absolute',
                top,
                left,
                width: isRecordMode ? '20px' : '24px',
                height: isRecordMode ? '20px' : '24px',
                pointerEvents: 'none',
                zIndex: 9999
              }}
            />
          );
        }

        if (losingKingSquare) {
          const { file: loseFile, rank: loseRank } = squareToCoordinates(losingKingSquare);
          const iconSize = isRecordMode ? 12 : 15;
          const { top, left } = getSmartIconPosition(loseFile, loseRank, squareSize, iconSize);
          
          overlayElements.push(
            <img
              key={`loser-king`}
              src={loserIcon}
              alt="Losing king"
              style={{
                position: 'absolute',
                top,
                left,
                width: isRecordMode ? '20px' : '24px',
                height: isRecordMode ? '20px' : '24px',
                pointerEvents: 'none',
                zIndex: 9999
              }}
            />
          );
        }
      }
      // Handle check scenario (but not checkmate)
      else if (gameState.isCheck && !gameState.isCheckmate) {
        // The king in check is the one whose turn it is in the current position
        const kingInCheckColor = game.turn() === 'w' ? 'white' : 'black';
        const kingInCheckSquare = kingPositions[kingInCheckColor];

        if (kingInCheckSquare) {
          const { file, rank } = squareToCoordinates(kingInCheckSquare);
          const iconSize = isRecordMode ? 12 : 15;
          const { top, left } = getSmartIconPosition(file, rank, squareSize, iconSize);
          
          overlayElements.push(
            <img
              key={`check-king`}
              src={checkIcon}
              alt="King in check"
              style={{
                position: 'absolute',
                top,
                left,
                width: isRecordMode ? '20px' : '24px',
                height: isRecordMode ? '20px' : '24px',
                pointerEvents: 'none',
                zIndex: 9999
              }}
            />
          );
        }
      }
      // Handle draw scenario
      else if (gameState.isDraw) {
        // Add draw icon to both kings
        [kingPositions.white, kingPositions.black].forEach((kingSquare, index) => {
          if (kingSquare) {
            const { file, rank } = squareToCoordinates(kingSquare);
            const iconSize = isRecordMode ? 12 : 15;
            const { top, left } = getSmartIconPosition(file, rank, squareSize, iconSize);
            
            overlayElements.push(
              <img
                key={`draw-king-${index}`}
                src={drawIcon}
                alt="Draw"
                style={{
                  position: 'absolute',
                  top,
                  left,
                  width: isRecordMode ? '20px' : '24px',
                  height: isRecordMode ? '20px' : '24px',
                  pointerEvents: 'none',
                  zIndex: 9999
                }}
              />
            );
          }
        });
      }
    }

    // Add Record mode classification icons
    if (isRecordMode && moveEnhancements && currentMove) {
      const currentEnhancements = moveEnhancements[currentMove];
      const classification = currentEnhancements?.classification;
      
      if (classification && classification.enabled && classification.iconId) {
        const iconSrc = STOCKFISH_ICON_MAP[classification.iconId];
        
        if (iconSrc) {
          // Get the move's target square for icon positioning
          let targetSquare = null;
          
          // Try to get target square from current move data
          if (moves && currentMove !== '-1') {
            const moveIndex = parseInt(currentMove, 10);
            if (!isNaN(moveIndex) && moveIndex >= 0 && moveIndex < moves.length) {
              targetSquare = moves[moveIndex]?.to;
            }
          }
          
          // If we have a target square, position the icon there
          if (targetSquare) {
            const { file, rank } = squareToCoordinates(targetSquare);
            const iconSize = 12; // Stockfish icons are always 12px offset
            const { top, left } = getSmartIconPosition(file, rank, squareSize, iconSize);
            
            overlayElements.push(
              <img
                key={`classification-${currentMove}`}
                src={iconSrc}
                alt={`${classification.iconName} move`}
                style={{
                  position: 'absolute',
                  top,
                  left,
                  width: '20px',
                  height: '20px',
                  pointerEvents: 'none',
                  zIndex: 9999
                }}
              />
            );
          }
        }
      }
    }

    // Add Record mode text overlays
    if (isRecordMode && moveEnhancements && currentMove) {
      const currentEnhancements = moveEnhancements[currentMove];
      const textOverlay = currentEnhancements?.textOverlay;
      
      if (textOverlay && textOverlay.enabled && textOverlay.text && textOverlay.text.trim() !== '') {
        // Get the move's target square for text positioning
        let targetSquare = null;
        
        // Try to get target square from current move data
        if (moves && currentMove !== '-1') {
          const moveIndex = parseInt(currentMove, 10);
          if (!isNaN(moveIndex) && moveIndex >= 0 && moveIndex < moves.length) {
            targetSquare = moves[moveIndex]?.to;
          }
        }
        
        // If we have a target square, position the text with edge handling
        if (targetSquare) {
          const { file, rank } = squareToCoordinates(targetSquare);
          
          // Edge handling logic
          let topPosition, leftPosition, transform;
          
          // Handle 1st rank (rank 7 in coordinate system) - text above the square
          if (rank === 7) {
            topPosition = `${rank * squareSize - 10}px`; // Above the square, moved down slightly
          } else {
            topPosition = `${rank * squareSize + squareSize - 6}px`; // Below the square
          }
          
          // Handle file positioning
          if (file === 0) {
            // A file - flush left
            leftPosition = `${file * squareSize}px`;
            transform = 'none';
          } else if (file === 7) {
            // H file - flush right
            leftPosition = `${file * squareSize + squareSize}px`;
            transform = 'translateX(-100%)';
          } else {
            // Center files - centered
            leftPosition = `${file * squareSize + (squareSize / 2)}px`;
            transform = 'translateX(-50%)';
          }
          
          overlayElements.push(
            <div
              key={`text-overlay-${currentMove}`}
              style={{
                position: 'absolute',
                top: topPosition,
                left: leftPosition,
                transform: transform,
                color: '#000', // Black font as specified
                fontSize: '14px',
                fontWeight: 'bold',
                textShadow: '0 0 2px #00FF00, 1px 1px 2px rgba(255, 255, 255, 0.8)', // Green outline with white shadow (thinner)
                // textShadow: '0 0 3px #FF0000, 1px 1px 2px rgba(255, 255, 255, 0.8)', // Red outline with white shadow
                // textShadow: '0 0 3px #FFD700, 1px 1px 2px rgba(255, 255, 255, 0.8)', // Gold outline with white shadow
                pointerEvents: 'none',
                zIndex: 999, // Very high z-index to ensure it appears above everything
                overflow: 'visible', // Allow text to extend beyond boundaries
                whiteSpace: 'nowrap', // Prevent text wrapping
                lineHeight: 1 // Tight line height
              }}
            >
              {textOverlay.text}
            </div>
          );
        }
      }
    }

    return overlayElements;
  };

  return (
    <div style={{ 
      borderRadius: '5px', 
      position: 'relative'
    }}>
      <Chessboard
        id="PlayableBoard"
        position={processedFEN()}
        onPieceDrop={onDrop}
        boardWidth={responsiveBoardSize}
        boardOrientation={orientation}
        customBoardStyle={{
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
        }}
        customDarkSquareStyle={boardTheme === 'custom' && customColors
          ? { background: customColors.dark }
          : currentTheme.dark}
        customLightSquareStyle={boardTheme === 'custom' && customColors
          ? { background: customColors.light }
          : currentTheme.light}
        showBoardNotation={shouldShowCoordinates}
        customSquareStyles={buildCustomStyles()}
        customArrows={getCombinedArrows()}
        onArrowsChange={onArrowsChange}
        onSquareClick={handleSquareClick}
        onSquareRightClick={handleSquareRightClick}
        // arePiecesDraggable={!isPuzzle || puzzleState === 'completed'}
      />
      {renderIconsOverlay()}
      
      {/* Player Info Components */}
      {/* White Player Info - Top Right */}
      {(showCollapsedIcons || playerInfoW.expanded) && (
        <div
          onClick={!playerInfoW.expanded ? handlePlayerInfoWClick : undefined}
          style={getPlayerInfoStyles(playerInfoW.expanded, 'top')}
        >
          {/* <i 
            className="fas fa-user" 
            style={{ 
              fontSize: playerInfoW.expanded ? '16px' : '12px', 
              color: '#000',
              minWidth: playerInfoW.expanded ? '16px' : '12px',
              marginRight: playerInfoW.expanded ? '8px' : '0',
              cursor: 'pointer'
            }}
            onClick={playerInfoW.expanded ? handlePlayerInfoWClick : undefined}
          /> */}
          {playerInfoW.expanded && (
            <>
              {isViewMode ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <span style={{ 
                    fontSize: isMobile ? '12px' : '14px', 
                    fontWeight: 'bold', 
                    color: '#000'
                  }}>
                    {cleanProfanity(playerInfoW.player) || 'Anonymous'}
                    {playerInfoW.rating && playerInfoW.rating !== '200?' && playerInfoW.rating.trim() !== '' && (
                      <span style={{ fontWeight: 'normal' }}> ({playerInfoW.rating})</span>
                    )}
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <input
                    type="text"
                    value={playerInfoW.player}
                    onChange={handlePlayerInfoWPlayerChange}
                    onClick={(e) => e.stopPropagation()}
                    style={getInputStyles('player')}
                    placeholder="Player Name"
                    maxLength={20}
                  />
                  <input
                    type="text"
                    value={playerInfoW.rating}
                    onChange={handlePlayerInfoWRatingChange}
                    onClick={(e) => e.stopPropagation()}
                    style={getInputStyles('rating')}
                    placeholder="Rating"
                    maxLength={4}
                  />
                </div>
              )}
              <i 
                className="fas fa-circle-xmark" 
                style={{ 
                  fontSize: isMobile ? '16px' : '20px', 
                  color: isViewMode ? '#ff0000' : '#000', // Red in ViewMode to indicate "close"
                  cursor: 'pointer',
                  marginLeft: isMobile ? '4px' : '8px'
                }}
                onClick={handlePlayerInfoWClick}
              />
            </>
          )}
        </div>
      )}

      {/* Black Player Info - Bottom Left */}
      {(showCollapsedIcons || playerInfoB.expanded) && (
        <div
          onClick={!playerInfoB.expanded ? handlePlayerInfoBClick : undefined}
          style={getPlayerInfoStyles(playerInfoB.expanded, 'bottom')}
        >
          {/* <i 
            className="fas fa-user" 
            style={{ 
              fontSize: playerInfoB.expanded ? '16px' : '12px', 
              color: '#000',
              minWidth: playerInfoB.expanded ? '16px' : '12px',
              marginRight: playerInfoB.expanded ? '8px' : '0',
              cursor: 'pointer'
            }}
            onClick={playerInfoB.expanded ? handlePlayerInfoBClick : undefined}
          /> */}
          {playerInfoB.expanded && (
            <>
              {isViewMode ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <span style={{ 
                    fontSize: isMobile ? '12px' : '14px', 
                    fontWeight: 'bold', 
                    color: '#000'
                  }}>
                    {cleanProfanity(playerInfoB.player) || 'Anonymous'}
                    {playerInfoB.rating && playerInfoB.rating !== '200?' && playerInfoB.rating.trim() !== '' && (
                      <span style={{ fontWeight: 'normal' }}> ({playerInfoB.rating})</span>
                    )}
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <input
                    type="text"
                    value={playerInfoB.player}
                    onChange={handlePlayerInfoBPlayerChange}
                    onClick={(e) => e.stopPropagation()}
                    style={getInputStyles('player')}
                    placeholder="Player Name"
                    maxLength={20}
                  />
                  <input
                    type="text"
                    value={playerInfoB.rating}
                    onChange={handlePlayerInfoBRatingChange}
                    onClick={(e) => e.stopPropagation()}
                    style={getInputStyles('rating')}
                    placeholder="Rating"
                    maxLength={4}
                  />
                </div>
              )}
              <i 
                className="fas fa-circle-xmark" 
                style={{ 
                  fontSize: isMobile ? '16px' : '20px', 
                  color: isViewMode ? '#ff0000' : '#000', // Red in ViewMode to indicate "close"
                  cursor: 'pointer',
                  marginLeft: isMobile ? '4px' : '8px'
                }}
                onClick={handlePlayerInfoBClick}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
});

// Export both Board and FENBuilderModal with the handleFENUpdate function
export { FENBuilderModal };
export default Board;